import { ChangeDetectionStrategy, Component, Inject, Self } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CommitteeEventAgendaService, UnsubscribeService } from '@common/services';
import { IAgendaItem, IAgendaOptions, IEmployeeOption, IRrule } from '@common/types';
import { IAgendaFormValue } from '@common/shared/components/form-groups/agenda/agenda.component';
import { BehaviorSubject, finalize, Observable, takeUntil, tap } from 'rxjs';
import { toISO } from '@common/dialogs/intersection-dialog/helpers/date.helpers';
import { addMinutes } from 'date-fns';

export type NoSystemAgendaProps = {
  eventId: string;
  editedAgendaId: string | null;
  title: string;
  agendaItems: IAgendaItem[];
  agendaOptions: IAgendaOptions;
  members: IEmployeeOption[];
  speakers: IEmployeeOption[];
  compromiseStart: string;
  rrule: IRrule;
};

@Component({
  selector: 'com-no-system-agenda',
  templateUrl: 'no-system-agenda.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UnsubscribeService]
})
export class NoSystemAgendaComponent {
  public checkAgendaIsValid: () => boolean;
  public agendaFormValue: IAgendaFormValue;

  public eventStart: string;
  public eventEnd: string;
  public loading$ = new BehaviorSubject(false);
  public loadIntersection$ = new BehaviorSubject(false);
  public invalid$ = new BehaviorSubject(false);
  public hasIntersection$ = new BehaviorSubject(false);

  constructor(
    public dialogRef: MatDialogRef<NoSystemAgendaComponent>,
    private eventAgendaService: CommitteeEventAgendaService,
    @Inject(MAT_DIALOG_DATA) public props: NoSystemAgendaProps,
    @Self() readonly unsubscribe: UnsubscribeService
  ) {
    this.initEventInfo();
  }

  private initEventInfo() {
    const { rrule, compromiseStart } = this.props;
    this.eventStart = toISO(compromiseStart);
    this.eventEnd = toISO(addMinutes(new Date(compromiseStart), rrule.duration));
  }

  save(): void {
    if (this.checkValid()) {
      this.loading$.next(true);
      (this.props.editedAgendaId ? this.edit() : this.create())
        .pipe(
          tap(() => this.dialogRef.close(true)),
          finalize(() => this.loading$.next(false)),
          takeUntil(this.unsubscribe)
        )
        .subscribe();
    }
  }

  create(): Observable<void> {
    const { agendaItems } = this.agendaFormValue;
    return this.eventAgendaService.add(
      agendaItems.find((a) => !a.id),
      this.props.eventId
    );
  }

  edit(): Observable<void> {
    const { agendaItems } = this.agendaFormValue;
    return this.eventAgendaService.edit(
      agendaItems.find((a) => a.id === this.props.editedAgendaId),
      this.props.eventId
    );
  }

  getAgendaValue(value: IAgendaFormValue): void {
    this.checkValid();
    this.agendaFormValue = value;
  }

  checkValid(): boolean {
    const valid = this.checkAgendaIsValid();
    this.invalid$.next(!valid);
    return valid;
  }
}
